import { useQuery } from "@tanstack/react-query";
import { useDataSource } from "@/data/DataProvider";
import { useIdentity } from "@/auth/ClientAuthProvider";
import type { ClientPolicy, PolicyClientSummary } from "@/data/types";

export type { ClientPolicy };

export function usePolicies() {
  const source = useDataSource();
  const id = useIdentity();
  return useQuery({
    queryKey: ["policies", id.orgId, id.leadId],
    queryFn: () => source.getPolicies(id),
    staleTime: 5 * 60_000,
  });
}

export function premiumDollars(p: Pick<ClientPolicy, "premium_cents" | "premium_amount">): number | null {
  if (p.premium_cents != null) return p.premium_cents / 100;
  if (p.premium_amount) return p.premium_amount;
  return null;
}

export function lifecycleLabel(status: string): { label: string; tone: "good" | "warn" | "muted" } {
  switch (status) {
    case "active":
      return { label: "Active", tone: "good" };
    case "pending_first_premium":
      return { label: "Waiting on first payment", tone: "warn" };
    case "lapsed":
      return { label: "Lapsed", tone: "warn" };
    case "surrendered":
      return { label: "Surrendered", tone: "muted" };
    case "claim_paid":
      return { label: "Claim paid", tone: "muted" };
    default:
      return { label: status.replace(/_/g, " "), tone: "muted" };
  }
}

export function frequencyLabel(f: PolicyClientSummary["payment_frequency"]): string {
  switch (f) {
    case "monthly":
      return "a month";
    case "quarterly":
      return "a quarter";
    case "semi_annual":
      return "every 6 months";
    case "annual":
      return "a year";
    case "single":
      return "one time";
    default:
      return "";
  }
}

export function productLabel(type: string): string {
  const t = type.toLowerCase();
  if (t.includes("iul") || t.includes("indexed universal")) return "Indexed Universal Life";
  if (t.includes("annuity")) return "Annuity";
  if (t.includes("whole")) return "Whole Life";
  if (t.includes("term")) return "Term Life";
  return type;
}
